import React, { useState, useEffect, useRef } from 'react';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import HistoryButton from '../components/HistoryButton';
import HistoryModal from '../components/HistoryModal';
import { progressService } from '../services/progressService';
import { InventoryService } from '../services/inventoryService';
import { useDialog } from '../context/DialogContext';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';

interface Step12Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step12: React.FC<Step12Props> = ({ user, onLogin, onLogout }) => {
  const { showAlert } = useDialog();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  
  // Action 3 state
  const [servicePlan, setServicePlan] = useState<string>('');
  const [reviewedWithSponsor, setReviewedWithSponsor] = useState<boolean>(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [savedPlan, setSavedPlan] = useState(false);
  const [savePlanError, setSavePlanError] = useState(false);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Read the SAA Green Book about Step Twelve",
      description: "See how spiritual awakening naturally leads me to carry the message and live recovery in all areas of life",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "Read guidance on working Step Twelve",
      description: "Read practical guidance on how to work Step Twelve effectively.",
      completed: false,
      hasButton: true,
      buttonText: "Read Guidance",
      buttonAction: () => setIsGuidanceOpen(true)
    },
    {
      id: 3,
      title: "Take Step Twelve: Carry the message to other sex addicts and practice these principles",
      description: "Write out your plan for service and carrying the message, then review it with your sponsor",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Twelve', path: '/saa/12-steps/step-12', active: true }
  ];

  // Initialize progress tracking
  const initializeProgress = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }
    
    setIsInitializing(true);
    
    try {
      // Initialize SAA progress
      await progressService.initializeProgress();
      
      // Fetch progress data
      const existingProgress = await progressService.getStepProgress('SAA', '12-steps', 12).catch(() => null);
      
      if (existingProgress) {
        const savedData = existingProgress.data || {};
        setStepData(existingProgress);
        
        // Restore actions state
        if (savedData.actions) {
          // Merge saved actions with original actions to restore missing properties
          const mergedActions = originalActions.map(originalAction => {
            const savedAction = savedData.actions.find((a: any) => a.id === originalAction.id);
            if (savedAction) {
              return {
                ...originalAction,
                completed: savedAction.completed,
                description: savedAction.description || originalAction.description
              };
            }
            return originalAction;
          });
          setActions(mergedActions);
        }
        
        // Load action 3 data from inventory service
        try {
          const inventoryData = await InventoryService.getInventory('step12-service-plan', '1.0');
          if (inventoryData && inventoryData.length > 0) {
            const planData = inventoryData[0];
            if (planData.plan) setServicePlan(planData.plan);
            if (planData.reviewedWithSponsor) setReviewedWithSponsor(planData.reviewedWithSponsor);
          }
        } catch (err) {
          console.error('Error loading service plan:', err);
        }
      }
      
      // Check date tracking preference
      try {
        const journeyData = await progressService.getJourneyProgress();
        setIsOptedOut(journeyData.opted_out);
      } catch (error) {
        console.error('Error loading date tracking preference:', error);
      }
    } catch (err: any) {
      console.error('Error initializing progress:', err);
      handleError(err, 'Failed to load your progress. Please try refreshing the page.');
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    initializeProgress();
  }, [user]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  // Auto-save service plan
  const saveServicePlan = async () => {
    if (!user) return;
    
    setSavingPlan(true);
    setSavedPlan(false);
    setSavePlanError(false);
    
    try {
      await InventoryService.saveInventory('step12-service-plan', '1.0', [{
        plan: servicePlan,
        reviewedWithSponsor: reviewedWithSponsor
      }]);
      setSavedPlan(true);
      setTimeout(() => setSavedPlan(false), 3000);
    } catch (err) {
      console.error('Error saving service plan:', err);
      setSavePlanError(true);
    } finally {
      setSavingPlan(false);
    }
  };

  const autoSaveServicePlan = () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      saveServicePlan();
    }, 2000);
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      handleError(new Error('User not logged in'), 'Please log in to save your progress.');
      return;
    }
    
    // Check if trying to complete action 3
    const action3 = updatedActions.find(a => a.id === 3);
    const currentAction3 = actions.find(a => a.id === 3);
    
    if (action3 && !currentAction3?.completed && action3.completed) {
      // Validate that plan has content and reviewed with sponsor checkbox is checked
      const planHasContent = servicePlan.trim().length > 0;
      if (!planHasContent || !reviewedWithSponsor) {
        showAlert({
          message: 'To complete this action, you must enter your service plan and check "Reviewed my plan with my sponsor".',
          variant: 'warning'
        });
        // Don't update actions - keep action 3 unchecked
        return;
      }
    }
    
    try {
      // Update local state
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 12,
        step_title: 'Step Twelve',
        completed: allCompleted,
        data: {
          actions: updatedActions
        }
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Twelve - ${completedCount} of 3 actions completed`,
        progress_milestone: allCompleted ? 'step12_completed' : 'step12_in_progress'
      });
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, 'Failed to save progress. Please try again.');
    }
  };

  // Render action 3 content
  const renderAction3Content = (action: ActionItem, index: number) => {
    if (action.id !== 3) return null;

    return (
      <div style={{ marginBottom: 16, width: '100%', paddingLeft: '56px' }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ 
            fontWeight: 600, 
            color: '#333', 
            fontSize: '14px',
            display: 'block',
            marginBottom: '8px'
          }}>
            My Plan for Service and Carrying the Message
          </label>
          <textarea
            value={servicePlan}
            onChange={(e) => {
              setServicePlan(e.target.value);
              autoSaveServicePlan();
            }}
            onBlur={saveServicePlan}
            placeholder="Write out your plan for service and carrying the message to others..."
            style={{
              width: '100%',
              minHeight: '120px',
              padding: '12px',
              border: '1px solid #ccc',
              borderRadius: 4,
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={reviewedWithSponsor}
              onChange={(e) => {
                setReviewedWithSponsor(e.target.checked);
                saveServicePlan();
              }}
              style={{ width: '16px', height: '16px' }}
            />
            <span style={{ fontWeight: 500, color: '#333' }}>
              Reviewed my plan with my sponsor
            </span>
          </label>
        </div>
        
        {/* Auto-save indicator */}
        <div style={{ 
          textAlign: 'right', 
          minHeight: 24,
          fontSize: '14px',
          fontWeight: 500
        }}>
          {savingPlan && (
            <span style={{ color: '#6c757d' }}>
              Saving...
            </span>
          )}
          {savedPlan && (
            <span style={{ color: '#28a745' }}>
              ✓ All changes saved
            </span>
          )}
          {savePlanError && (
            <span style={{ color: '#dc3545' }}>
              ⚠ Save failed. Please try again.
            </span>
          )}
        </div>
      </div>
    );
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step Twelve</h1>
            {stepData && (
              <HistoryButton
                stepNumber={12}
                stepTitle="Step Twelve"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            Having had a spiritual awakening as the result of these steps, we tried to carry this message to sex addicts and to practice these principles in all our affairs.
          </p>
        </div>

        {isInitializing && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
              </>
            )}
          </div>
        )}

        {!user && (
          <StepLoginPrompt onLogin={onLogin} />
        )}

        {!isInitializing && !errorMessage && user && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>Actions to Complete Step Twelve</h2>
            <p className={styles.actionsDescription}>
              Complete each action in order. Check the box when you've finished each action.
            </p>
            
            <StepActions
              actions={actions}
              onActionChange={handleActionChange}
              stepNumber={12}
              renderActionContent={renderAction3Content}
            />

            {allCompleted && (
              <div className={styles.completionMessage}>
                <h3>🎉 Congratulations!</h3>
                <p>🎉 Congratulations! You have completed all 12 Steps of SAA. You are now ready to continue your recovery journey and help others.</p>
              </div>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                                  <a href="/saa/12-steps/step-11" className={styles.prevStepLink}>
                  {'<<'} Step Eleven
                </a>
                  <span></span>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-twelve start>>"
        endTag="<<step-twelve end>>"
      />

      {/* ContentReader for How to Work Step Twelve */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_12_Guidance.md"
        startTag="<<working-step-twelve start>>"
        endTag="<<working-step-twelve end>>"
        title="Guidance on working Step Twelve"
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={12}
        stepTitle="Step Twelve"
        stepData={stepData}
        onStepUpdated={() => {
          setIsHistoryModalOpen(false);
          // Refresh step data
          if (user) {
            initializeProgress();
          }
        }}
      />

    </div>
  );
};

export default Step12; 